/**
 * Chat store — conversación con Claude inline.
 *
 * Persiste mensajes en localStorage por sesión. Reset al logout.
 * El backend espera historial Array<{role, content}> con máximo 20 mensajes.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Máximo de mensajes que se persisten en localStorage (los más recientes). */
const MAX_PERSISTED_MESSAGES = 30

/**
 * MP5-FRONT-CONFIRMAR (96b62f45) — contrato orquestador→front, shape ratificado
 * MF-SHAPE-RATIFICADO. El back lo expone como `data.propuesta_entidad` en la respuesta
 * del POST /chat (campo top-level dedicado: la UI NO escarba la traza). Dos fases:
 *   - 'preview'  → proponer_entidad (PURO): semáforo C1 en vivo mientras se itera, sin botones.
 *   - 'borrador' → crear_borrador_entidad: ya hay wizard_id + acciones; salen los 2 botones.
 * El VALIDAR/DESCARTAR los dispara la sesión del HUMANO (gate 1); las rutas vienen CON su
 * versión resuelta server-side (§33.5) — el front NUNCA construye ruta ni versión.
 */
export interface AccionRuta {
  method: string // 'POST' (transición canónica)
  path: string // /canonico/<sub>/<token>/<estado>/vX.Y/instancias/<id>/transiciones — versión del back
  body: Record<string, unknown> // { verbo: 'VALIDAR' } | { verbo: 'DESCARTAR' }
}
export interface PropuestaEntidad {
  fase: 'preview' | 'borrador'
  wizard_id?: string
  resumen: { nombre: string; n_estados: number; n_transiciones: number; n_campos: number; n_indices: number }
  /** C1 del molde-compositor: verde global (ok) + los 3 chequeos BSK-4. */
  semaforo: { ok: boolean; loop?: boolean; terminal?: boolean; descarte_s13?: boolean }
  /** Solo en fase 'borrador'. */
  acciones?: { confirmar: AccionRuta; descartar: AccionRuta }
  /** Campos que el back exige en el DESCARTAR (DMN validar_descarte_borrador), p.ej. ['razon_descarte']. */
  campos_requeridos?: string[]
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  ts: number
  tokens?: { input?: number; output?: number }
  latencia_ms?: number
  /** IHC (f13222da): ids de instancias que el agente consultó en esta respuesta (de la
   *  traza). El front ofrece sobre cada una los controles permitidos (AccionesInstancia). */
  instancias?: string[]
  /** MP5-FRONT-CONFIRMAR: propuesta de entidad del orquestador (preview o borrador).
   *  NO se persiste (ver partialize) — es efímera de la sesión viva: tras recargar, un
   *  borrador ya validado/descartado no debe revivir sus botones. */
  propuesta?: PropuestaEntidad
  /** F3 Inc 4: id del informe-html.generado (estado generado) nacido de este turno (§16). Cuando
   *  está presente, el front abre el informe GOBERNADO (su html_resultado), no la copia inline. */
  informeId?: string
  /** CHAT-TIER1 #1 (trazabilidad visible): los pasos de tool-use que el agente ejecutó en este
   *  turno (de la `traza` de POST /chat). Hace visible la frontera dual: el agente LEE (estas
   *  consultas) y PROPONE; las acciones las confirma el humano y el servidor las revalida. */
  traza?: Array<{ tool: string; input?: unknown; ok?: boolean }>
}

interface ChatState {
  open: boolean
  loading: boolean
  mensajes: ChatMessage[]
  incluirContexto: boolean
  /** F3: hilo gobernado activo (conversacion.iniciada). Se reenvía en el POST /chat para
   *  continuar el mismo hilo; null = el próximo turno abre uno nuevo. Persiste (recargar
   *  la página sigue el mismo hilo); "Limpiar conversación" lo resetea (hilo nuevo). */
  conversacionId: string | null
  /** F3: agente-ia.registrado elegido en el selector de modelo. null = default del back. */
  agenteId: string | null

  setOpen: (v: boolean) => void
  toggle: () => void
  setLoading: (v: boolean) => void
  append: (m: ChatMessage) => void
  reset: () => void
  setIncluirContexto: (v: boolean) => void
  setConversacionId: (v: string | null) => void
  setAgenteId: (v: string | null) => void
  /** F3 Inc 2: recuperar un hilo — reemplaza los mensajes y fija el conversacion_id activo. */
  setHilo: (conversacionId: string, mensajes: ChatMessage[]) => void
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      open: false,
      loading: false,
      mensajes: [],
      incluirContexto: true,
      conversacionId: null,
      agenteId: null,

      setOpen(v) { set({ open: v }) },
      toggle() { set({ open: !get().open }) },
      setLoading(v) { set({ loading: v }) },
      append(m) { set({ mensajes: [...get().mensajes, m] }) },
      reset() { set({ mensajes: [], conversacionId: null }) },
      setIncluirContexto(v) { set({ incluirContexto: v }) },
      setConversacionId(v) { set({ conversacionId: v }) },
      setAgenteId(v) { set({ agenteId: v }) },
      setHilo(conversacionId, mensajes) { set({ conversacionId, mensajes }) },
    }),
    {
      name: 'arquitecto-chat',
      // La propuesta NO se persiste: un borrador ya resuelto no debe revivir sus botones
      // al recargar (el estado real lo da el back; aquí solo vive en la sesión activa).
      partialize: (s) => ({
        mensajes: s.mensajes.slice(-MAX_PERSISTED_MESSAGES).map(({ propuesta: _omit, ...m }) => m),
        incluirContexto: s.incluirContexto,
        // F3: el hilo activo y el modelo elegido sobreviven a un reload (continúa el mismo
        // hilo gobernado). El "Limpiar conversación" los resetea (hilo nuevo).
        conversacionId: s.conversacionId,
        agenteId: s.agenteId,
      }),
    },
  ),
)
